import React, { useState, useRef, useMemo, useCallback } from "react";
import {
  Popover,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Collapse,
  Divider,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import styled from "styled-components";
import { colors } from "../theme/colors";

const GroupedSitePicker = ({
  siteGroups = [],
  allSites = [],
  customerId,
  selectedGroupIds = [],
  selectedIndividualSites = [],
  onGroupSelectionChange,
  onIndividualSiteSelectionChange,
  placeholder = "Select sites",
  disabled = false,
  loading = false,
}) => {
  const [open, setOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState("");
  const anchorRef = useRef(null);

  // Set of all site IDs that belong to at least one group
  const groupedSiteIds = useMemo(() => {
    const ids = new Set();
    siteGroups.forEach((group) => {
      (group.sites || []).forEach((site) => ids.add(site.site_id));
    });
    return ids;
  }, [siteGroups]);

  // Ungrouped sites = allSites whose site_id is NOT in any group
  const ungroupedSites = useMemo(() => {
    return allSites.filter((site) => !groupedSiteIds.has(site.site_id));
  }, [allSites, groupedSiteIds]);

  // Check state for each group
  const groupCheckStates = useMemo(() => {
    const states = {};
    siteGroups.forEach((group) => {
      if (selectedGroupIds.includes(group.value)) {
        states[group.value] = "checked";
      } else {
        const groupSiteIds = (group.sites || []).map((s) => s.site_id);
        const selectedCount = groupSiteIds.filter((siteId) =>
          selectedIndividualSites.some(
            ([c, s]) => c === customerId && s === siteId,
          ),
        ).length;
        if (selectedCount === 0) {
          states[group.value] = "unchecked";
        } else if (selectedCount === groupSiteIds.length) {
          states[group.value] = "all_individual";
        } else {
          states[group.value] = "indeterminate";
        }
      }
    });
    return states;
  }, [siteGroups, selectedGroupIds, selectedIndividualSites, customerId]);

  // Filter groups and sites by search term
  const lowerSearch = searchTerm.toLowerCase().trim();

  const filteredGroups = useMemo(() => {
    if (!lowerSearch) return siteGroups;
    return siteGroups
      .map((group) => {
        const groupMatches = group.label.toLowerCase().includes(lowerSearch);
        const matchingSites = (group.sites || []).filter((site) =>
          site.site_name.toLowerCase().includes(lowerSearch),
        );
        if (groupMatches || matchingSites.length > 0) {
          return { ...group, _matchingSites: matchingSites };
        }
        return null;
      })
      .filter(Boolean);
  }, [siteGroups, lowerSearch]);

  const filteredUngrouped = useMemo(() => {
    if (!lowerSearch) return ungroupedSites;
    return ungroupedSites.filter((site) =>
      site.label.toLowerCase().includes(lowerSearch),
    );
  }, [ungroupedSites, lowerSearch]);

  // Auto-expand groups whose child sites match the search
  const effectiveExpanded = useMemo(() => {
    if (!lowerSearch) return expandedGroups;
    const expanded = new Set(expandedGroups);
    filteredGroups.forEach((group) => {
      if (
        group._matchingSites &&
        group._matchingSites.length > 0 &&
        !group.label.toLowerCase().includes(lowerSearch)
      ) {
        expanded.add(group.value);
      }
    });
    return expanded;
  }, [expandedGroups, filteredGroups, lowerSearch]);

  // Check if a site is selected (either via group or individually)
  const isSiteSelected = useCallback(
    (siteId) => {
      // Check if site's group is selected
      for (const group of siteGroups) {
        if (
          selectedGroupIds.includes(group.value) &&
          (group.sites || []).some((s) => s.site_id === siteId)
        ) {
          return true;
        }
      }
      // Check individual selection
      return selectedIndividualSites.some(
        ([c, s]) => c === customerId && s === siteId,
      );
    },
    [siteGroups, selectedGroupIds, selectedIndividualSites, customerId],
  );

  // Check if a site is disabled (its group is selected)
  const isSiteDisabledByGroup = useCallback(
    (siteId) => {
      for (const group of siteGroups) {
        if (
          selectedGroupIds.includes(group.value) &&
          (group.sites || []).some((s) => s.site_id === siteId)
        ) {
          return true;
        }
      }
      return false;
    },
    [siteGroups, selectedGroupIds],
  );

  const handleOpen = () => {
    if (!disabled && !loading) {
      setOpen(true);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setSearchTerm("");
  };

  const handleExpandToggle = (e, groupId) => {
    e.stopPropagation();
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  };

  const handleGroupToggle = (groupId) => {
    const isCurrentlySelected = selectedGroupIds.includes(groupId);
    if (isCurrentlySelected) {
      // Uncheck group — sites become unselected
      onGroupSelectionChange(selectedGroupIds.filter((id) => id !== groupId));
    } else {
      // Check group — add to selectedGroupIds, remove individual site selections for this group's sites
      const group = siteGroups.find((g) => g.value === groupId);
      const groupSiteIds = new Set(
        (group?.sites || []).map((s) => s.site_id),
      );
      const cleanedIndividual = selectedIndividualSites.filter(
        ([custId, siteId]) =>
          !(custId === customerId && groupSiteIds.has(siteId)),
      );
      onGroupSelectionChange([...selectedGroupIds, groupId]);
      onIndividualSiteSelectionChange(cleanedIndividual);
    }
  };

  const handleSiteToggle = (siteId) => {
    const isSelected = selectedIndividualSites.some(
      ([c, s]) => c === customerId && s === siteId,
    );
    if (isSelected) {
      onIndividualSiteSelectionChange(
        selectedIndividualSites.filter(
          ([c, s]) => !(c === customerId && s === siteId),
        ),
      );
    } else {
      onIndividualSiteSelectionChange([
        ...selectedIndividualSites,
        [customerId, siteId],
      ]);
    }
  };

  const handleSelectAll = () => {
    // Select all groups
    const allGroupIds = siteGroups.map((g) => g.value);
    onGroupSelectionChange(allGroupIds);

    // Select all ungrouped sites (filter out any that are in groups since groups are now selected)
    const ungroupedSelections = ungroupedSites.map((site) => [
      customerId,
      site.site_id,
    ]);
    // Keep only ungrouped individual selections since all groups are selected
    onIndividualSiteSelectionChange(ungroupedSelections);
  };

  const handleClearAll = () => {
    onGroupSelectionChange([]);
    onIndividualSiteSelectionChange(
      selectedIndividualSites.filter(([c]) => c !== customerId),
    );
  };

  const handleClear = (e) => {
    e.stopPropagation();
    handleClearAll();
  };

  // Build display value
  const displayValue = useMemo(() => {
    const groupLabels = siteGroups
      .filter((g) => selectedGroupIds.includes(g.value))
      .map((g) => g.label);

    const individualLabels = selectedIndividualSites
      .filter(([c]) => c === customerId)
      .map(([, siteId]) => {
        const site = allSites.find((s) => s.site_id === siteId);
        return site ? site.label : siteId;
      });

    const all = [...groupLabels, ...individualLabels];
    if (all.length === 0) return null;
    if (all.length <= 3) return all.join(", ");
    return `${groupLabels.length > 0 ? `${groupLabels.length} group${groupLabels.length > 1 ? "s" : ""}` : ""}${groupLabels.length > 0 && individualLabels.length > 0 ? ", " : ""}${individualLabels.length > 0 ? `${individualLabels.length} site${individualLabels.length > 1 ? "s" : ""}` : ""}`;
  }, [
    siteGroups,
    selectedGroupIds,
    selectedIndividualSites,
    customerId,
    allSites,
  ]);

  const hasSelections =
    selectedGroupIds.length > 0 ||
    selectedIndividualSites.some(([c]) => c === customerId);

  return (
    <PickerWrapper>
      <TriggerButton
        ref={anchorRef}
        onClick={handleOpen}
        $disabled={disabled}
        $open={open}
        $hasValue={!!displayValue}
      >
        <DisplayText $hasValue={!!displayValue}>
          {displayValue || placeholder}
        </DisplayText>
        <ButtonActions>
          {hasSelections && !disabled && (
            <ClearButton
              aria-label="clear selection"
              onClick={handleClear}
              size="small"
            >
              <CloseIcon fontSize="small" />
            </ClearButton>
          )}
          <DropdownArrow $open={open}>
            <ExpandMoreIcon fontSize="small" />
          </DropdownArrow>
        </ButtonActions>
      </TriggerButton>

      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
        slotProps={{
          paper: {
            style: {
              width: anchorRef.current
                ? anchorRef.current.offsetWidth
                : "auto",
              minWidth: 280,
              maxHeight: 400,
              borderRadius: 12,
              marginTop: 4,
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
            },
          },
        }}
      >
        <PopoverContent>
          {/* Search field */}
          <SearchContainer>
            <StyledTextField
              size="small"
              placeholder="Search sites..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      fontSize="small"
                      style={{ color: colors.neutral[500] }}
                    />
                  </InputAdornment>
                ),
              }}
              autoFocus
            />
          </SearchContainer>

          <ScrollableList>
            {/* Site Groups */}
            {filteredGroups.map((group) => {
              const checkState = groupCheckStates[group.value] || "unchecked";
              const isExpanded = effectiveExpanded.has(group.value);
              const sitesToShow = lowerSearch
                ? group._matchingSites || group.sites || []
                : group.sites || [];

              return (
                <React.Fragment key={group.value}>
                  <GroupRow>
                    <ListItemButton
                      dense
                      onClick={() => handleGroupToggle(group.value)}
                      style={{ paddingRight: 8 }}
                    >
                      <ListItemIcon style={{ minWidth: 36 }}>
                        <Checkbox
                          edge="start"
                          checked={checkState === "checked"}
                          indeterminate={
                            checkState === "indeterminate" ||
                            checkState === "all_individual"
                          }
                          disableRipple
                          style={{
                            color:
                              checkState === "unchecked"
                                ? colors.neutral[500]
                                : colors.primary.main,
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={group.label}
                        primaryTypographyProps={{
                          fontSize: 14,
                          fontWeight: 600,
                          color: colors.neutral[800],
                        }}
                      />
                      <SiteCount>
                        {(group.sites || []).length} site
                        {(group.sites || []).length !== 1 ? "s" : ""}
                      </SiteCount>
                      <ExpandButton
                        size="small"
                        onClick={(e) => handleExpandToggle(e, group.value)}
                      >
                        {isExpanded ? (
                          <ExpandMoreIcon fontSize="small" />
                        ) : (
                          <ChevronRightIcon fontSize="small" />
                        )}
                      </ExpandButton>
                    </ListItemButton>
                  </GroupRow>

                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <ChildList>
                      {sitesToShow.map((site) => {
                        const siteSelected = isSiteSelected(site.site_id);
                        const disabledByGroup = isSiteDisabledByGroup(
                          site.site_id,
                        );

                        return (
                          <ChildRow
                            key={site.site_id}
                            dense
                            onClick={() => {
                              if (!disabledByGroup)
                                handleSiteToggle(site.site_id);
                            }}
                            disabled={disabledByGroup}
                          >
                            <ListItemIcon style={{ minWidth: 36 }}>
                              <Checkbox
                                edge="start"
                                checked={siteSelected}
                                disabled={disabledByGroup}
                                disableRipple
                                size="small"
                                style={{
                                  color: siteSelected
                                    ? colors.primary.main
                                    : colors.neutral[400],
                                }}
                              />
                            </ListItemIcon>
                            <ListItemText
                              primary={site.site_name}
                              primaryTypographyProps={{
                                fontSize: 13,
                                color: disabledByGroup
                                  ? colors.neutral[400]
                                  : colors.neutral[700],
                              }}
                            />
                          </ChildRow>
                        );
                      })}
                    </ChildList>
                  </Collapse>
                </React.Fragment>
              );
            })}

            {/* Divider between groups and ungrouped */}
            {filteredGroups.length > 0 && filteredUngrouped.length > 0 && (
              <StyledDivider />
            )}

            {/* Ungrouped Sites */}
            {filteredUngrouped.length > 0 && (
              <>
                {filteredGroups.length > 0 && (
                  <SectionHeader>Ungrouped Sites</SectionHeader>
                )}
                {filteredUngrouped.map((site) => {
                  const siteSelected = selectedIndividualSites.some(
                    ([c, s]) => c === customerId && s === site.site_id,
                  );

                  return (
                    <ListItemButton
                      key={site.site_id}
                      dense
                      onClick={() => handleSiteToggle(site.site_id)}
                    >
                      <ListItemIcon style={{ minWidth: 36 }}>
                        <Checkbox
                          edge="start"
                          checked={siteSelected}
                          disableRipple
                          style={{
                            color: siteSelected
                              ? colors.primary.main
                              : colors.neutral[500],
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={site.label}
                        primaryTypographyProps={{
                          fontSize: 14,
                          color: colors.neutral[800],
                        }}
                      />
                    </ListItemButton>
                  );
                })}
              </>
            )}

            {/* Empty state */}
            {filteredGroups.length === 0 && filteredUngrouped.length === 0 && (
              <EmptyState>
                {lowerSearch ? "No sites match your search" : "No sites available"}
              </EmptyState>
            )}
          </ScrollableList>

          {/* Footer */}
          <FooterContainer>
            <FooterButton onClick={handleSelectAll}>Select All</FooterButton>
            <FooterButton onClick={handleClearAll}>Clear All</FooterButton>
          </FooterContainer>
        </PopoverContent>
      </Popover>
    </PickerWrapper>
  );
};

// Styled Components

const PickerWrapper = styled.div`
  width: 100%;
`;

const TriggerButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 48px;
  padding: 12px 12px 12px 20px;
  border: 2px solid ${(props) => (props.$open ? colors.primary.main : "#e9ecef")};
  border-radius: 20px;
  background: ${(props) => (props.$disabled ? colors.neutral[100] : "white")};
  cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};
  opacity: ${(props) => (props.$disabled ? 0.6 : 1)};
  transition: all 0.3s ease;
  box-sizing: border-box;

  ${(props) =>
    props.$open &&
    `
    box-shadow: 0 0 0 3px rgba(44, 90, 160, 0.1);
  `}

  &:hover {
    border-color: ${(props) => (props.$disabled ? "#e9ecef" : colors.primary.main)};
  }
`;

const DisplayText = styled.span`
  color: ${(props) => (props.$hasValue ? "#333" : "#999")};
  font-size: 14px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
`;

const ButtonActions = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  margin-left: 8px;
  flex-shrink: 0;
`;

const ClearButton = styled(IconButton)`
  && {
    padding: 4px;
    color: #878787;

    &:hover {
      color: ${colors.primary.main};
      background-color: rgba(44, 90, 160, 0.08);
    }
  }
`;

const DropdownArrow = styled.div`
  display: flex;
  align-items: center;
  color: #878787;
  transition: transform 0.2s ease;
  transform: ${(props) => (props.$open ? "rotate(180deg)" : "rotate(0deg)")};
`;

const PopoverContent = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 380px;
`;

const SearchContainer = styled.div`
  padding: 8px 12px;
  border-bottom: 1px solid ${colors.neutral[200]};
`;

const StyledTextField = styled(TextField)`
  && {
    .MuiOutlinedInput-root {
      border-radius: 8px;
      font-size: 13px;

      fieldset {
        border-color: ${colors.neutral[200]};
      }

      &:hover fieldset {
        border-color: ${colors.primary.main};
      }

      &.Mui-focused fieldset {
        border-color: ${colors.primary.main};
        border-width: 1px;
      }
    }
  }
`;

const ScrollableList = styled(List)`
  && {
    overflow-y: auto;
    flex: 1;
    padding: 4px 0;
    max-height: 280px;
  }
`;

const GroupRow = styled.div`
  background: ${colors.siteGroup.background};
  border-left: 3px solid ${colors.siteGroup.color};
  margin: 2px 8px;
  border-radius: 6px;

  .MuiListItemButton-root:hover {
    background: ${colors.siteGroup.backgroundHover};
  }
`;

const SiteCount = styled.span`
  font-size: 12px;
  color: ${colors.neutral[500]};
  margin-right: 4px;
  white-space: nowrap;
`;

const ExpandButton = styled(IconButton)`
  && {
    padding: 4px;
    color: ${colors.neutral[600]};
  }
`;

const ChildList = styled.div`
  padding-left: 24px;
`;

const ChildRow = styled(ListItemButton)`
  && {
    padding-left: 20px;
    border-radius: 4px;
    margin: 0 8px;

    &.Mui-disabled {
      opacity: 0.6;
    }
  }
`;

const StyledDivider = styled(Divider)`
  && {
    margin: 4px 16px;
  }
`;

const SectionHeader = styled.div`
  padding: 8px 16px 4px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${colors.neutral[500]};
`;

const EmptyState = styled.div`
  padding: 24px 16px;
  text-align: center;
  color: ${colors.neutral[500]};
  font-size: 13px;
`;

const FooterContainer = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 8px 12px;
  border-top: 1px solid ${colors.neutral[200]};
  background: ${colors.neutral[100]};
  border-radius: 0 0 12px 12px;
`;

const FooterButton = styled.button`
  background: none;
  border: none;
  color: ${colors.primary.main};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: background 0.2s;

  &:hover {
    background: rgba(44, 90, 160, 0.08);
  }
`;

export default GroupedSitePicker;
